import { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { PropertyProfileSectionId } from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { formatLocaleDateTime } from '@/lib/format-utils'
import { SOURCE_LABELS, sectionChanges, sectionDef } from '@/lib/profile'

interface ProfileRevisionsSheetProps {
  propertyId: string
  sectionId: PropertyProfileSectionId | null
  onClose: () => void
}

function RevisionsList({
  propertyId,
  sectionId,
}: {
  propertyId: string
  sectionId: PropertyProfileSectionId
}) {
  const query = useInfiniteQuery({
    queryKey: ['property-profile-revisions', propertyId, sectionId],
    queryFn: ({ pageParam }) =>
      api.getPropertyProfileRevisions(propertyId, sectionId, pageParam ?? null),
    initialPageParam: null as string | null,
    getNextPageParam: last => last.nextCursor ?? null,
  })

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        <LoadingSkeleton className="h-16 w-full" />
        <LoadingSkeleton className="h-16 w-full" />
      </div>
    )
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} compact />
  }
  const revisions = query.data?.pages.flatMap(page => page.items) ?? []
  if (revisions.length === 0) {
    return <p className="text-sm text-textMuted">Nobody has saved this section yet.</p>
  }

  return (
    <ol className="space-y-3">
      {revisions.map(revision => {
        const changes = sectionChanges(sectionId, revision.before, revision.after)
        return (
          <li key={revision.id} className="rounded-card border border-border p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-textMuted">
              <Badge variant={revision.actor_type === 'ops' ? 'default' : 'secondary'}>
                {SOURCE_LABELS[revision.actor_type]}
              </Badge>
              {/* An ops actor is an Access email; an owner's id is not shown. */}
              {revision.actor_type === 'ops' && (
                <span className="wrap-break-word text-text">{revision.actor_id}</span>
              )}
              <span>{formatLocaleDateTime(revision.created_at)}</span>
              {revision.before === null && <span>first save</span>}
            </div>
            {changes.length === 0 ? (
              <p className="mt-2 text-sm text-textMuted">Saved with no change to the answers.</p>
            ) : (
              <dl className="mt-2 space-y-2 text-sm">
                {changes.map(change => (
                  <div key={change.key}>
                    <dt className="font-medium text-text">{change.label}</dt>
                    <dd className="wrap-break-word text-textMuted">
                      <span className="line-through decoration-textFaint">{change.before}</span>
                      <span aria-hidden> -&gt; </span>
                      <span className="sr-only"> changed to </span>
                      <span className="text-text">{change.after}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        )
      })}
      {query.hasNextPage && (
        <li>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading...' : 'Show older saves'}
          </Button>
        </li>
      )}
    </ol>
  )
}

/** A section's save history (CAP-1467): who, when, and each answer old -> new. */
export function ProfileRevisionsSheet({
  propertyId,
  sectionId: openSection,
  onClose,
}: ProfileRevisionsSheetProps) {
  // Keep the last section while the sheet slides out, so it does not blank.
  const [shown, setShown] = useState<PropertyProfileSectionId | null>(openSection)
  if (openSection && openSection !== shown) setShown(openSection)
  const sectionId = openSection ?? shown
  return (
    <Sheet open={openSection !== null} onOpenChange={open => !open && onClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>History: {sectionId ? sectionDef(sectionId).title : ''}</SheetTitle>
          <SheetDescription>Every save of this section, newest first.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {sectionId && <RevisionsList propertyId={propertyId} sectionId={sectionId} />}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
